/* eslint-disable simple-import-sort/imports */
import { useState } from "react";
import { Root } from "react-dom/client";

import * as turf from "@turf/turf";

import {
  ActionCreatorWithPayload,
  Dispatch,
  UnknownAction
} from "@reduxjs/toolkit";

import { Map } from "maplibre-gl";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";

import { EditIcon, TrashIcon } from "@/assets/icons";
import { MAX_AREA } from "@/constants/map";
import { PolygonSettingItem } from "@/types/spatial";
import { truncateGeoJson } from "@/utils/map";
import { geoJSONToWKT } from "@/utils/wkt";

import MapboxDraw from "@mapbox/mapbox-gl-draw";

import { Button } from "../ui/button";
import { DatasetDetailSessionStorageProps } from "../datasetManagement/details/ActionPanel/FormActionPanel";
import useSessionStorage from "@/hooks/store/useSessionStorage";

const TEST_ID_PREFIX = "custom-polygon-popup";

export interface PolygonPopupProps {
  dispatch: Dispatch<UnknownAction>;
  updatePolygonSettingsTemp: ActionCreatorWithPayload<
    Partial<PolygonSettingItem> & Pick<PolygonSettingItem, "id">,
    "spatial/updatePolygonSettingsTemp"
  >;
  areaCalculate: number;
  feature: Feature<Geometry, GeoJsonProperties>;
  popup: maplibregl.Popup | null;
  deleteFeature: (featureId: string) => void;
  draw: MapboxDraw | null;
  rootRef: { current: Root } | null;
  map?: Map | null;
  isCustomDrawPolygonDataset?: boolean;
  setDrawPolygonDataset?: ActionCreatorWithPayload<
    {
      name?: string;
      polygon: Feature<Geometry, GeoJsonProperties>;
    },
    "spatial/setDrawPolygonDataset"
  >;
}

export default function PolygonPopup({
  dispatch,
  updatePolygonSettingsTemp,
  areaCalculate,
  feature,
  popup,
  deleteFeature,
  draw,
  rootRef,
  map,
  isCustomDrawPolygonDataset,
  setDrawPolygonDataset
}: PolygonPopupProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [name, setName] = useState(feature.properties?.name);
  const { sessionValue: sessionStorageData } = useSessionStorage(
    "dataset-detail-draw-polygon",
    {} as DatasetDetailSessionStorageProps
  );

  const isAreaTooLarge =
    areaCalculate >
    (Number(import.meta.env.VITE_MAX_DRAW_AREA_SIZE) || MAX_AREA);

  const resetPopup = () => {
    popup?.remove();
    rootRef?.current?.unmount();
  };

  const handleClose = () => {
    if (isAreaTooLarge) deleteFeature(feature.id as string);
    resetPopup();
  };

  const handleDelete = () => {
    resetPopup();
    deleteFeature(feature.id as string);
  };

  const handleSave = () => {
    resetPopup();
    if (!draw || !feature?.id) return;
    const newName =
      name || `Location layer polygon #${feature.properties?.sequence}`;

    draw.setFeatureProperty(feature.id as string, "name", newName);

    const selectedFeature = draw.getSelected()?.features?.[0];
    if (!selectedFeature) return;

    const truncatedPolygon = truncateGeoJson(selectedFeature);
    if (map && isCustomDrawPolygonDataset && setDrawPolygonDataset) {
      const bounds = turf.bbox(truncatedPolygon);
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]]
        ],
        { padding: 50 }
      );

      dispatch(
        setDrawPolygonDataset({
          name: sessionStorageData?.name,
          polygon: truncatedPolygon
        })
      );
      return;
    }
    dispatch(
      updatePolygonSettingsTemp({
        id: feature.id as string,
        name: newName,
        sequence: feature.properties?.sequence,
        polygon: truncatedPolygon
      })
    );
  };

  return (
    <div
      id="polygon-popup"
      className="bg-white p-3 rounded-xl min-w-[352px] font-sans"
    >
      {isEdit ? (
        <input
          type="text"
          className="w-full border border-gainsboro rounded-lg p-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      ) : (
        <div className="flex items-center gap-2">
          <h1 className="text-txtHeading font-semibold font-sans leading-[22px]">
            {name}
          </h1>
          <button
            className="!w-4 !h-4 !p-0"
            type="button"
            aria-label={`Edit polygon ${name}`}
            onClick={() => setIsEdit(true)}
          >
            <EditIcon />
          </button>
        </div>
      )}
      <p className="text-txtBody text-[10px] font-sans leading-4">
        This custom polygon made by custom, to accommodate specific area, do not
        cater by system.{" "}
      </p>

      <div className="grid grid-cols-2 my-3 border border-gainsboro rounded-lg overflow-hidden">
        <div className="bg-white-smoke py-2 px-3 flex items-center  border-b border-gainsboro">
          <p className="text-txtBody text-xs font-semibold leading-[22px]">
            Polygon
          </p>
        </div>
        <div className="py-2 px-3 border-b border-gainsboro">
          <p className="truncate overflow-hidden break-all whitespace-pre-line line-clamp-3">
            {geoJSONToWKT(feature)?.replace(/^POLYGON\s*/i, "")}
          </p>
        </div>

        <div className="bg-white-smoke py-2 px-3 flex items-center">
          <p className="text-txtBody text-xs font-semibold leading-[22px]">
            Area
          </p>
        </div>
        <div className="py-2 px-3">
          <p>
            {areaCalculate.toLocaleString(undefined, {
              maximumFractionDigits: 1
            })}{" "}
            km².
          </p>
        </div>
      </div>

      {isAreaTooLarge && (
        <p className="text-cinnabar text-[10px] font-sans leading-4 -mt-2 mb-3">
          Maximum area is{" "}
          {Number(import.meta.env.VITE_MAX_DRAW_AREA_SIZE).toLocaleString()}{" "}
          km². Please reduce the size.
        </p>
      )}

      <div className="flex items-center flex-row gap-2">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="font-sans font-semibold py-1 px-4 h-auto"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="default"
            className="font-sans font-semibold py-1 px-4 h-auto"
            disabled={isAreaTooLarge}
            onClick={handleSave}
            data-testid={`${TEST_ID_PREFIX}-save-button`}
          >
            Save
          </Button>
        </div>

        <button
          className="font-sans ml-auto font-semibold py-1 px-4 h-auto"
          onClick={handleDelete}
          aria-label={`Delete polygon ${name}`}
        >
          <TrashIcon className="fill-txtBody" />
        </button>
      </div>
    </div>
  );
}
